import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck, ChartBar, UsersThree, CalendarBlank, CreditCard, ChatCircleDots,
  SignOut, ArrowsClockwise, MagnifyingGlass, Phone,
} from "@phosphor-icons/react";

import { adminClient, getAdminToken, clearAdminToken } from "@/lib/adminApi";
import { toast } from "sonner";

const TABS = [
  { key: "stats", label: "Overview", icon: ChartBar },
  { key: "leads", label: "Leads", icon: UsersThree },
  { key: "bookings", label: "Bookings", icon: CalendarBlank },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "chat", label: "Chat sessions", icon: ChatCircleDots },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtMoney = (amount, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount || 0);

function Stat({ label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-zinc-900 border border-white/10 rounded-2xl p-5 hover:border-blue-500/40 transition-colors"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-400">{label}</div>
      <div className="mt-2 text-3xl font-heading font-extrabold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

function StatusBadge({ value }) {
  const v = (value || "").toLowerCase();
  let cls = "bg-zinc-700 text-zinc-200";
  if (["paid", "complete", "synced", "ok"].some((k) => v.includes(k)))
    cls = "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30";
  else if (["pending", "initiated", "processing"].some((k) => v.includes(k)))
    cls = "bg-amber-500/20 text-amber-300 border border-amber-500/30";
  else if (["failed", "error", "cancelled", "canceled"].some((k) => v.includes(k)))
    cls = "bg-rose-500/20 text-rose-300 border border-rose-500/30";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {value || "—"}
    </span>
  );
}

function StatSkeleton() {
  return (
    <div
      data-testid="stat-skeleton"
      className="bg-zinc-900 border border-white/10 rounded-2xl p-5 animate-pulse"
    >
      <div className="h-3 w-20 rounded bg-white/10" />
      <div className="mt-3 h-9 w-24 rounded bg-white/10" />
      <div className="mt-2 h-3 w-32 rounded bg-white/5" />
    </div>
  );
}

function BlogSyncCard() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try {
      const { data } = await adminClient().get("/admin/blog-sync/status");
      setStatus(data);
    } catch { /* silent */ }
  };
  React.useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient().post("/admin/blog-sync/run");
      if (data?.ok) toast.success(`Blog sync ok · ${data.articleCount} articles`);
      else toast.error(`Blog sync failed: ${data?.error || "unknown"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Blog sync failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="blog-sync-card"
      className="bg-zinc-900 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4"
    >
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-400">Blog sync</div>
        <div className="mt-2 font-heading text-lg font-bold text-white">
          {status?.articleCount != null ? `${status.articleCount} articles` : "—"}
        </div>
        <div className="mt-1 text-xs text-zinc-500">
          Last run: {status?.lastRun ? fmtDate(status.lastRun) : "never"}
        </div>
      </div>
      <button
        onClick={run}
        disabled={busy}
        data-testid="blog-sync-run"
        className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white px-3 py-1.5 text-sm transition-colors"
      >
        <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
        Run now
      </button>
    </div>
  );
}

function OverviewPanel({ stats, loading }) {
  if (loading || !stats) {
    return (
      <div className="space-y-8">
        {["Leads", "Bookings", "Payments & Chat"].map((label) => (
          <section key={label}>
            <h3 className="text-sm uppercase tracking-widest text-zinc-400">{label}</h3>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <StatSkeleton key={i} />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm uppercase tracking-widest text-zinc-400">Leads</h3>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat testid="stat-leads-total" label="Total" value={stats.leads.total} />
          <Stat testid="stat-leads-24h" label="Last 24h" value={stats.leads.last24h} />
          <Stat testid="stat-leads-7d" label="Last 7 days" value={stats.leads.last7d} />
          <Stat testid="stat-leads-30d" label="Last 30 days" value={stats.leads.last30d} />
        </div>
      </section>

      <section>
        <h3 className="text-sm uppercase tracking-widest text-zinc-400">Bookings</h3>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat testid="stat-bookings-total" label="Total" value={stats.bookings.total} />
          <Stat testid="stat-bookings-24h" label="Last 24h" value={stats.bookings.last24h} />
          <Stat testid="stat-bookings-7d" label="Last 7 days" value={stats.bookings.last7d} />
          <Stat testid="stat-bookings-30d" label="Last 30 days" value={stats.bookings.last30d} />
        </div>
      </section>

      <section>
        <h3 className="text-sm uppercase tracking-widest text-zinc-400">Payments & Chat</h3>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat testid="stat-payments-paid" label="Paid sessions" value={stats.payments.paid} sub={`of ${stats.payments.total} created`} />
          <Stat testid="stat-revenue" label="Revenue" value={fmtMoney(stats.payments.revenue, stats.payments.currency)} sub="paid sessions" />
          <Stat testid="stat-chat-sessions" label="Chat sessions" value={stats.chat.sessions} />
          <Stat testid="stat-chat-messages" label="Chat messages" value={stats.chat.messages} />
        </div>
      </section>

      <section>
        <h3 className="text-sm uppercase tracking-widest text-zinc-400">Blog auto-sync</h3>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <BlogSyncCard />
        </div>
      </section>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="relative mb-4">
      <MagnifyingGlass
        size={18}
        weight="bold"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
      />
      <input
        data-testid="admin-search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-zinc-900 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

function StatusCell({ status, onRetry, retrying, title }) {
  const failed = ["failed", "error", "not_configured"].some((k) =>
    (status || "").toLowerCase().includes(k)
  );
  return (
    <div className="inline-flex items-center gap-1.5">
      <StatusBadge value={status} />
      {failed && (
        <button
          onClick={onRetry}
          disabled={retrying}
          title={title}
          data-testid={`retry-${title || "btn"}`}
          className="h-6 w-6 rounded-md bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-300 hover:text-white grid place-items-center transition-colors"
        >
          <ArrowsClockwise
            size={12}
            weight="bold"
            className={retrying ? "animate-spin" : ""}
          />
        </button>
      )}
    </div>
  );
}

function LeadsTable({ items, onResync, retrying }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm text-left">
        <thead className="bg-zinc-900 text-xs uppercase tracking-widest text-zinc-400">
          <tr>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Contact</th>
            <th className="px-4 py-3">Service</th>
            <th className="px-4 py-3">Source / Promo</th>
            <th className="px-4 py-3">Pipedrive</th>
            <th className="px-4 py-3">GHL</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5 bg-zinc-950">
          {items.map((l) => (
            <tr key={l.id} data-testid={`row-lead-${l.id}`} className="hover:bg-white/[0.02]">
              <td className="px-4 py-3 whitespace-nowrap text-zinc-400">{fmtDate(l.createdAt)}</td>
              <td className="px-4 py-3 font-medium text-white">
                {[l.firstName, l.lastName].filter(Boolean).join(" ") || "—"}
              </td>
              <td className="px-4 py-3 text-zinc-300">
                <a
                  href={`mailto:${l.email}`}
                  className="text-blue-400 hover:text-blue-300 block truncate max-w-[200px]"
                >
                  {l.email}
                </a>
                <a
                  href={`tel:${(l.phone || "").replace(/\D/g, "")}`}
                  className="text-zinc-500 hover:text-white text-xs inline-flex items-center gap-1"
                >
                  <Phone size={11} weight="bold" /> {l.phone}
                </a>
              </td>
              <td className="px-4 py-3 text-zinc-200 max-w-[240px] truncate">{l.service}</td>
              <td className="px-4 py-3 text-zinc-400">{l.promo || "—"}</td>
              <td className="px-4 py-3">
                <StatusCell
                  status={l.crmStatus}
                  onRetry={() => onResync(l.id, "pipedrive")}
                  retrying={retrying === `${l.id}:pipedrive` || retrying === `${l.id}:both`}
                  title={`pipedrive-${l.id}`}
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <StatusCell
                    status={l.ghlStatus}
                    onRetry={() => onResync(l.id, "ghl")}
                    retrying={retrying === `${l.id}:ghl` || retrying === `${l.id}:both`}
                    title={`ghl-${l.id}`}
                  />
                  {l.ghlPipeline && l.ghlStatus === "synced" && (
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {l.ghlPipeline} pipeline
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                No leads yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BookingsTable({ items }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm text-left">
        <thead className="bg-zinc-900 text-xs uppercase tracking-widest text-zinc-400">
          <tr>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Date · Time</th>
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Contact</th>
            <th className="px-4 py-3">Service</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5 bg-zinc-950">
          {items.map((b) => (
            <tr key={b.id} data-testid={`row-booking-${b.id}`} className="hover:bg-white/[0.02]">
              <td className="px-4 py-3 whitespace-nowrap text-zinc-400">{fmtDate(b.createdAt)}</td>
              <td className="px-4 py-3 font-medium text-white whitespace-nowrap">
                {b.date} · {b.timeSlot}
              </td>
              <td className="px-4 py-3 text-zinc-200">
                {[b.firstName, b.lastName].filter(Boolean).join(" ")}
              </td>
              <td className="px-4 py-3 text-zinc-400 text-xs">
                <div className="truncate max-w-[200px]">{b.email}</div>
                <div>{b.phone}</div>
              </td>
              <td className="px-4 py-3 text-zinc-200 max-w-[240px] truncate">{b.service}</td>
              <td className="px-4 py-3">
                <StatusBadge value={b.status} />
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-zinc-500">
                No bookings yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTable({ items }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm text-left">
        <thead className="bg-zinc-900 text-xs uppercase tracking-widest text-zinc-400">
          <tr>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Package</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Session</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5 bg-zinc-950">
          {items.map((p) => (
            <tr key={p.sessionId} data-testid={`row-payment-${p.sessionId}`} className="hover:bg-white/[0.02]">
              <td className="px-4 py-3 whitespace-nowrap text-zinc-400">{fmtDate(p.createdAt)}</td>
              <td className="px-4 py-3 text-zinc-200">{p.metadata?.package_label || p.packageId}</td>
              <td className="px-4 py-3 font-semibold text-white">{fmtMoney(p.amount, p.currency)}</td>
              <td className="px-4 py-3 text-zinc-400">{p.customerEmail || "—"}</td>
              <td className="px-4 py-3 text-zinc-500 text-xs font-mono truncate max-w-[160px]">
                {p.sessionId}
              </td>
              <td className="px-4 py-3">
                <StatusBadge value={p.paymentStatus} />
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-zinc-500">
                No payments yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ChatTable({ items }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm text-left">
        <thead className="bg-zinc-900 text-xs uppercase tracking-widest text-zinc-400">
          <tr>
            <th className="px-4 py-3">Last activity</th>
            <th className="px-4 py-3">Started</th>
            <th className="px-4 py-3">Messages</th>
            <th className="px-4 py-3">Session ID</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5 bg-zinc-950">
          {items.map((s) => (
            <tr
              key={s.sessionId}
              data-testid={`row-chat-${s.sessionId}`}
              className="hover:bg-white/[0.02]"
            >
              <td className="px-4 py-3 whitespace-nowrap text-zinc-200">{fmtDate(s.lastAt)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-zinc-400">{fmtDate(s.firstAt)}</td>
              <td className="px-4 py-3 text-zinc-200">{s.messages}</td>
              <td className="px-4 py-3 text-zinc-500 text-xs font-mono truncate max-w-[260px]">{s.sessionId}</td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={`/admin/chat-sessions/${encodeURIComponent(s.sessionId)}`}
                  data-testid={`chat-view-${s.sessionId}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-semibold transition-colors"
                >
                  View transcript
                </Link>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                No chat sessions yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [chat, setChat] = useState([]);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [me, setMe] = useState(null);
  const [retrying, setRetrying] = useState(""); // `${leadId}:${target}` or ""
  const [bulkProgress, setBulkProgress] = useState(null); // { target, done, total, succeeded }
  const hasToken = !!getAdminToken();

  const fetchAll = async () => {
    setBusy(true);
    try {
      const api = adminClient();
      const [meR, statsR, lR, bR, pR, cR] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/stats"),
        api.get("/admin/leads?limit=500"),
        api.get("/admin/bookings?limit=500"),
        api.get("/admin/payments?limit=500"),
        api.get("/admin/chat-sessions?limit=100"),
      ]);
      setMe(meR.data);
      setStats(statsR.data);
      setLeads(lR.data.items || []);
      setBookings(bR.data.items || []);
      setPayments(pR.data.items || []);
      setChat(cR.data.items || []);
    } catch (err) {
      console.error("admin fetch error:", err);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (hasToken) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  const filteredLeads = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.firstName, l.lastName, l.email, l.phone, l.service, l.promo, l.crmStatus]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [leads, query]);

  const filteredBookings = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return bookings;
    return bookings.filter((b) =>
      [b.firstName, b.lastName, b.email, b.phone, b.service, b.status, b.date]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [bookings, query]);

  const filteredPayments = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return payments;
    return payments.filter((p) =>
      [p.packageId, p.customerEmail, p.sessionId, p.paymentStatus, p.metadata?.package_label]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [payments, query]);

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  const resyncLead = async (leadId, target = "both") => {
    setRetrying(`${leadId}:${target}`);
    try {
      const { data } = await adminClient().post(
        `/admin/leads/${leadId}/resync?crm=${encodeURIComponent(target)}`
      );
      const fresh = data?.lead;
      if (fresh) {
        setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...fresh } : l)));
        const okPd = fresh.crmStatus === "synced";
        const okGhl = fresh.ghlStatus === "synced";
        if (target === "pipedrive") {
          okPd ? toast.success("Pipedrive synced") : toast.error("Pipedrive still failing — check token");
        } else if (target === "ghl") {
          okGhl ? toast.success("GoHighLevel synced") : toast.error("GoHighLevel still failing");
        } else {
          toast.success(
            `Resynced — Pipedrive: ${fresh.crmStatus || "?"} · GHL: ${fresh.ghlStatus || "?"}`
          );
        }
      }
      return data?.lead;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resync failed");
      return null;
    } finally {
      setRetrying("");
    }
  };

  const bulkResync = async (target) => {
    if (bulkProgress) return;
    const statusKey = target === "ghl" ? "ghlStatus" : "crmStatus";
    const targets = leads.filter((l) =>
      ["failed", "error", "not_configured"].some((k) =>
        (l[statusKey] || "").toLowerCase().includes(k)
      )
    );
    if (targets.length === 0) {
      toast.info(`No failed ${target === "ghl" ? "GoHighLevel" : "Pipedrive"} leads.`);
      return;
    }

    setBulkProgress({ target, done: 0, total: targets.length, succeeded: 0 });
    let succeeded = 0;
    for (let i = 0; i < targets.length; i++) {
      const lead = targets[i];
      try {
        const { data } = await adminClient().post(
          `/admin/leads/${lead.id}/resync?crm=${encodeURIComponent(target)}`
        );
        const fresh = data?.lead;
        if (fresh) {
          setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...fresh } : l)));
          if (fresh[statusKey] === "synced") succeeded++;
        }
      } catch {
        // continue — show summary at end
      }
      setBulkProgress((p) => (p ? { ...p, done: i + 1, succeeded } : null));
    }

    const label = target === "ghl" ? "GoHighLevel" : "Pipedrive";
    if (succeeded === targets.length) {
      toast.success(`${label}: ${succeeded}/${targets.length} resynced.`);
    } else if (succeeded > 0) {
      toast.success(`${label}: ${succeeded}/${targets.length} resynced — ${targets.length - succeeded} still failing.`);
    } else {
      toast.error(`${label}: 0/${targets.length} resynced. Check the token.`);
    }
    setBulkProgress(null);
  };

  const failedPipedrive = leads.filter((l) =>
    ["failed", "error", "not_configured"].some((k) => (l.crmStatus || "").toLowerCase().includes(k))
  ).length;
  const failedGhl = leads.filter((l) =>
    ["failed", "error", "not_configured"].some((k) => (l.ghlStatus || "").toLowerCase().includes(k))
  ).length;

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex">
      <Helmet>
        <title>Admin · TechConnect LLC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Sidebar */}
      <aside className="w-60 bg-zinc-900 border-r border-white/10 flex flex-col">
        <div className="p-5 flex items-center gap-2 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-blue-600 grid place-items-center">
            <ShieldCheck size={16} weight="fill" />
          </div>
          <span className="font-heading text-sm font-bold tracking-tight">Admin</span>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-blue-600 text-white"
                    : "text-zinc-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2">
          <div className="text-xs text-zinc-500 truncate px-1">{me?.email || ""}</div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-300 hover:bg-rose-500/15 hover:text-rose-200 transition-colors"
          >
            <SignOut size={16} weight="bold" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col">
        <header className="border-b border-white/10 bg-zinc-950 px-8 py-4 flex items-center justify-between">
          <h1 className="font-heading text-xl font-bold capitalize">
            {TABS.find((t) => t.key === activeTab)?.label}
          </h1>
          <button
            onClick={fetchAll}
            disabled={busy}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 hover:bg-zinc-800 px-3 py-1.5 text-sm transition-colors"
          >
            <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
            Refresh
          </button>
        </header>

        <main className="flex-1 overflow-auto p-8">
          {activeTab === "stats" && <OverviewPanel stats={stats} loading={busy && !stats} />}
          {activeTab === "leads" && (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                  onClick={() => bulkResync("pipedrive")}
                  disabled={!!bulkProgress || failedPipedrive === 0}
                  data-testid="bulk-resync-pipedrive"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 text-xs font-semibold transition-colors"
                  title={`Resync ${failedPipedrive} failed Pipedrive lead${failedPipedrive === 1 ? "" : "s"}`}
                >
                  <ArrowsClockwise
                    size={12}
                    weight="bold"
                    className={bulkProgress?.target === "pipedrive" ? "animate-spin" : ""}
                  />
                  {bulkProgress?.target === "pipedrive"
                    ? `Pipedrive ${bulkProgress.done}/${bulkProgress.total}…`
                    : `Resync failed Pipedrive (${failedPipedrive})`}
                </button>
                <button
                  onClick={() => bulkResync("ghl")}
                  disabled={!!bulkProgress || failedGhl === 0}
                  data-testid="bulk-resync-ghl"
                  className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 text-xs font-semibold transition-colors"
                  title={`Resync ${failedGhl} failed GHL lead${failedGhl === 1 ? "" : "s"}`}
                >
                  <ArrowsClockwise
                    size={12}
                    weight="bold"
                    className={bulkProgress?.target === "ghl" ? "animate-spin" : ""}
                  />
                  {bulkProgress?.target === "ghl"
                    ? `GHL ${bulkProgress.done}/${bulkProgress.total}…`
                    : `Resync failed GHL (${failedGhl})`}
                </button>
                {bulkProgress && (
                  <span data-testid="bulk-progress" className="text-xs text-zinc-400 ml-1">
                    {bulkProgress.succeeded} of {bulkProgress.done} succeeded so far
                  </span>
                )}
              </div>
              <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search by name, email, phone, service, promo…"
              />
              <LeadsTable items={filteredLeads} onResync={resyncLead} retrying={retrying} />
            </>
          )}
          {activeTab === "bookings" && (
            <>
              <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search by name, email, service, status, date…"
              />
              <BookingsTable items={filteredBookings} />
            </>
          )}
          {activeTab === "payments" && (
            <>
              <SearchBar
                value={query}
                onChange={setQuery}
                placeholder="Search by package, customer email, session ID, status…"
              />
              <PaymentsTable items={filteredPayments} />
            </>
          )}
          {activeTab === "chat" && <ChatTable items={chat} />}
        </main>
      </div>
    </div>
  );
}
